import { useState, useEffect, useRef } from 'react'
import { 
  Tag, 
  Plus, 
  X, 
  StickyNote, 
  Check, 
  Loader2, 
  AlertCircle,
  Clock,
  Star,
  Heart,
  Zap,
  Flag,
  BookOpen,
  Users
} from 'lucide-react'
import { minoriApi } from '../../services/api'

// Colori predefiniti per i tag
const TAG_COLORS = [
  { name: 'gray', bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', ring: 'ring-gray-400' },
  { name: 'red', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', ring: 'ring-red-400' },
  { name: 'orange', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', ring: 'ring-orange-400' },
  { name: 'yellow', bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', ring: 'ring-yellow-400' },
  { name: 'green', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', ring: 'ring-green-400' },
  { name: 'blue', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', ring: 'ring-blue-400' },
  { name: 'purple', bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', ring: 'ring-purple-400' },
  { name: 'pink', bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-700 dark:text-pink-300', ring: 'ring-pink-400' }
]

// Tag suggeriti comuni
const SUGGESTED_TAGS = [
  { label: 'DSA', color: 'blue', icon: BookOpen },
  { label: 'BES', color: 'purple', icon: Star },
  { label: 'Allergie', color: 'red', icon: AlertCircle },
  { label: 'Trasporto', color: 'orange', icon: Clock },
  { label: 'Priorità', color: 'yellow', icon: Flag },
  { label: 'Nuovo', color: 'green', icon: Zap },
  { label: 'Famiglia', color: 'pink', icon: Users },
  { label: 'Attenzione', color: 'red', icon: Heart }
]

/**
 * QuickTags - Componente per gestire tag rapidi su un minore
 */
export function QuickTags({ 
  minoreId, 
  initialTags = [], 
  onTagsChange,
  editable = true,
  compact = false 
}) {
  const [tags, setTags] = useState(initialTags)
  const [isAdding, setIsAdding] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [selectedColor, setSelectedColor] = useState('blue')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    setTags(initialTags)
  }, [initialTags])

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isAdding])

  const getColorClasses = (colorName) => {
    return TAG_COLORS.find(c => c.name === colorName) || TAG_COLORS[0]
  }

  const handleAddTag = async () => {
    if (!newTag.trim()) {
      setIsAdding(false)
      return
    }

    const tag = {
      label: newTag.trim(),
      color: selectedColor,
      createdAt: new Date().toISOString()
    }

    setSaving(true)
    try {
      // Se c'è un minoreId, salva sul backend
      if (minoreId) {
        await minoriApi.update(minoreId, {
          tags: [...tags, tag]
        })
      }
      
      const newTags = [...tags, tag]
      setTags(newTags)
      onTagsChange?.(newTags)
      setNewTag('')
      setIsAdding(false)
    } catch (error) {
      console.error('Errore salvataggio tag:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveTag = async (index) => {
    setSaving(true)
    try {
      const newTags = tags.filter((_, i) => i !== index)
      
      if (minoreId) {
        await minoriApi.update(minoreId, { tags: newTags })
      }
      
      setTags(newTags)
      onTagsChange?.(newTags)
    } catch (error) {
      console.error('Errore rimozione tag:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleSuggestedTag = (suggested) => {
    setNewTag(suggested.label)
    setSelectedColor(suggested.color)
    setIsAdding(true)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    } else if (e.key === 'Escape') {
      setIsAdding(false)
      setNewTag('')
    }
  }

  if (compact) {
    return (
      <div className="flex flex-wrap gap-1">
        {tags.map((tag, idx) => {
          const colors = getColorClasses(tag.color)
          return (
            <span
              key={idx}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}
            >
              {tag.label}
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Tags esistenti */}
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, idx) => {
          const colors = getColorClasses(tag.color)
          return (
            <span
              key={idx}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text} group`}
            >
              <Tag className="h-3 w-3" />
              {tag.label}
              {editable && (
                <button
                  onClick={() => handleRemoveTag(idx)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-600"
                  disabled={saving}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          )
        })}

        {/* Add button */}
        {editable && !isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors"
          >
            <Plus className="h-3 w-3" />
            Aggiungi tag
          </button>
        )}
      </div>

      {/* Add tag form */}
      {isAdding && (
        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-3">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nome tag..."
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              onClick={handleAddTag}
              disabled={saving || !newTag.trim()}
              className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </button>
            <button
              onClick={() => { setIsAdding(false); setNewTag(''); }}
              className="px-3 py-1.5 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Color picker */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Colore:</span>
            <div className="flex gap-1">
              {TAG_COLORS.map(color => (
                <button
                  key={color.name}
                  onClick={() => setSelectedColor(color.name)}
                  className={`w-6 h-6 rounded-full ${color.bg} ${
                    selectedColor === color.name ? `ring-2 ${color.ring} ring-offset-2` : ''
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Suggested tags */}
          {tags.length < 3 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Suggeriti:</p>
              <div className="flex flex-wrap gap-1">
                {SUGGESTED_TAGS
                  .filter(s => !tags.some(t => t.label === s.label))
                  .slice(0, 4)
                  .map(suggested => {
                    const colors = getColorClasses(suggested.color)
                    const Icon = suggested.icon
                    return (
                      <button
                        key={suggested.label}
                        onClick={() => handleSuggestedTag(suggested)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${colors.bg} ${colors.text} hover:ring-2 ${colors.ring} transition-all`}
                      >
                        <Icon className="h-3 w-3" />
                        {suggested.label}
                      </button>
                    )
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * QuickNotes - Componente per note rapide su un minore
 */
export function QuickNotes({
  minoreId,
  initialNotes = [],
  onNotesChange,
  maxNotes = 5
}) {
  const [notes, setNotes] = useState(initialNotes)
  const [isAdding, setIsAdding] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [saving, setSaving] = useState(false)
  const textareaRef = useRef(null)

  useEffect(() => {
    setNotes(initialNotes)
  }, [initialNotes])

  useEffect(() => {
    if (isAdding && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [isAdding])

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      setIsAdding(false)
      return
    }

    const note = {
      text: newNote.trim(),
      createdAt: new Date().toISOString(),
      author: 'Utente' // In produzione, usare il nome dell'utente loggato
    }

    setSaving(true)
    try {
      if (minoreId) {
        await minoriApi.update(minoreId, {
          quickNotes: [...notes, note]
        })
      }
      
      const newNotes = [...notes, note]
      setNotes(newNotes)
      onNotesChange?.(newNotes)
      setNewNote('')
      setIsAdding(false)
    } catch (error) {
      console.error('Errore salvataggio nota:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveNote = async (index) => {
    setSaving(true)
    try {
      const newNotes = notes.filter((_, i) => i !== index)
      
      if (minoreId) {
        await minoriApi.update(minoreId, { quickNotes: newNotes })
      }
      
      setNotes(newNotes)
      onNotesChange?.(newNotes)
    } catch (error) {
      console.error('Errore rimozione nota:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <StickyNote className="h-4 w-4 text-yellow-500" />
          Note Rapide
        </h4>
        {notes.length < maxNotes && !isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            <Plus className="h-4 w-4" />
            Aggiungi
          </button>
        )}
      </div>

      {/* Notes list */}
      {notes.length > 0 ? (
        <div className="space-y-2">
          {notes.map((note, idx) => (
            <div
              key={idx}
              className="relative p-3 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 rounded-r-lg group"
            >
              <p className="text-sm text-gray-700 dark:text-gray-300 pr-6">
                {note.text}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {new Date(note.createdAt).toLocaleDateString('it-IT')}
                {note.author && ` • ${note.author}`}
              </p>
              <button
                onClick={() => handleRemoveNote(idx)}
                className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
                disabled={saving}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      ) : !isAdding && (
        <p className="text-sm text-gray-400 italic">Nessuna nota rapida</p>
      )}

      {/* Add note form */}
      {isAdding && (
        <div className="space-y-2">
          <textarea
            ref={textareaRef}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Scrivi una nota rapida..."
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setIsAdding(false); setNewNote(''); }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Annulla
            </button>
            <button
              onClick={handleAddNote}
              disabled={saving || !newNote.trim()}
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Salva
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * TagFilter - Componente per filtrare per tag
 */
export function TagFilter({ availableTags = [], selectedTags = [], onChange }) {
  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      onChange(selectedTags.filter(t => t !== tag))
    } else {
      onChange([...selectedTags, tag])
    }
  }

  if (availableTags.length === 0) return null

  return (
    <div className="flex flex-wrap gap-1">
      {availableTags.map((tag, idx) => {
        const colors = TAG_COLORS.find(c => c.name === tag.color) || TAG_COLORS[0]
        const isSelected = selectedTags.includes(tag.label)
        
        return (
          <button
            key={idx}
            onClick={() => toggleTag(tag.label)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
              isSelected 
                ? `${colors.bg} ${colors.text} ring-2 ${colors.ring}` 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Tag className="h-3 w-3" />
            {tag.label}
          </button>
        )
      })}
    </div>
  )
}
